import { Link } from "react-router-dom";
import { Terminal, ArrowLeft } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const PAGES = {
  terms: {
    title: "Terms of Use",
    subtitle: "Rules of the road for using deployd.",
    sections: [
      {
        h: "01 · Acceptance",
        p: "By accessing deployd.'s website, feeds and dashboards, you agree to these Terms of Use. If you do not agree, please do not use the service.",
      },
      {
        h: "02 · Use of the service",
        p: "deployd. is a repository of verified customer case studies for enterprise technology and AI. You may use it for legitimate research and evaluation. You must not scrape, resell or misrepresent the content.",
      },
      {
        h: "03 · Content ownership",
        p: "Each case study is the intellectual property of the vendor and customer who authored and verified it. deployd. curates, verifies and formats — it does not claim ownership of the underlying content.",
      },
      {
        h: "04 · Verification",
        p: "A case study labelled 'Customer Verified' has had its headline metrics reviewed with the customer's team before publication. deployd. is not liable for any operational decision made on the basis of a case study — please do your own due diligence.",
      },
      {
        h: "05 · Account access",
        p: "Access to the industry feed requires an official work email. Free-provider email addresses (gmail, yahoo, hotmail, etc.) are not accepted. Providing false information is grounds for revoking access.",
      },
      {
        h: "06 · Changes",
        p: "We may update these terms as the platform evolves. Continued use after changes constitutes acceptance.",
      },
      {
        h: "07 · Contact",
        p: "Questions? Email hello@deploydnow.com.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    subtitle: "What we collect, why, and how we handle it.",
    sections: [
      {
        h: "01 · Data we collect",
        p: "When you sign up, we collect: your official work email, name, company, designation, industry and company size. When you submit a case study, we also store the content you provide.",
      },
      {
        h: "02 · Why we collect it",
        p: "To curate an industry-specific feed for you, to verify submissions with the appropriate customer, and to send you the promised updates. We do not sell personal data to third parties.",
      },
      {
        h: "03 · Storage",
        p: "Data is stored in a MongoDB database in a secure cloud environment. Passwords (if applicable) are hashed with bcrypt.",
      },
      {
        h: "04 · Deletion",
        p: "You can request deletion of your personal data at any time by writing to hello@deploydnow.com. We will comply within 30 days.",
      },
      {
        h: "05 · Cookies",
        p: "We use minimal browser storage (localStorage) to remember your session on this device. We do not run behavioural ad trackers.",
      },
    ],
  },
  cookies: {
    title: "Cookie Notice",
    subtitle: "Small print about small data.",
    sections: [
      {
        h: "01 · What we use",
        p: "deployd. uses browser localStorage to keep you signed in on this device. We do not set third-party advertising cookies.",
      },
      {
        h: "02 · How to clear",
        p: "Signing out from the top-right of any page clears the local session immediately. Alternatively, clear your browser storage for this site.",
      },
    ],
  },
  about: {
    title: "About deployd.",
    subtitle: "Why we built the only repository of verified AI use cases.",
    sections: [
      {
        h: "01 · The problem",
        p: "Enterprise tech buyers are drowning in vendor claims — glossy PDFs, unnamed 'Fortune 500' logos, and analyst reports behind paywalls. There is no honest, industry-specific view of what tools are actually deployed and what actually worked.",
      },
      {
        h: "02 · What deployd. is",
        p: "The first independent repository of AI and enterprise-technology case studies verified with both the vendor and the customer. Real stacks. Real outcomes. The parts that broke.",
      },
      {
        h: "03 · Who it's for",
        p: "Technology leaders — CIOs, CTOs, VP Engineering, VP Operations — who evaluate and deploy modern software stacks, and the vendors whose success stories deserve to be discovered by the right buyers.",
      },
      {
        h: "04 · Contact",
        p: "hello@deploydnow.com · San Francisco · Follow us on LinkedIn @deploydnow",
      },
    ],
  },
};

export default function LegalPage({ slug }) {
  const data = PAGES[slug];
  if (!data) return null;
  return (
    <main className="relative min-h-screen w-full bg-[#F7F6F1] text-zinc-900">
      <div className="grain-overlay" />
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-emerald-700"
        >
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / {slug}
        </Link>
        <Link
          to="/"
          className="group inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-emerald-700"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Home
        </Link>
      </header>
      <section className="relative z-10 mx-auto w-full max-w-3xl px-6 pb-24 pt-14 sm:px-10 sm:pt-20">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.32em] text-emerald-600">
          // {slug}
        </p>
        <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-5xl">
          {data.title}
        </h1>
        <p className="mt-3 font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-500">
          {data.subtitle} &middot; last updated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" })}
        </p>
        <div className="mt-10 space-y-8">
          {data.sections.map((s, i) => (
            <div key={i} className="border-t border-zinc-200 pt-6">
              <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-700">
                {s.h}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-zinc-700 sm:text-lg">
                {s.p}
              </p>
            </div>
          ))}
        </div>
      </section>
      <footer className="relative z-10 border-t border-zinc-200 px-6 py-6 sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <span>
            © {new Date().getFullYear()} <BrandMark size="inline" />, Inc.
          </span>
          <span>San Francisco · hello@deploydnow.com</span>
        </div>
      </footer>
    </main>
  );
}
